/* eslint-disable array-callback-return */
import { Device, DeviceStatusSummary, Shipment } from 'types';
import styles from './DeviceStatusSummaryCell.module.scss';
import {
    getDeviceStatusColor,
    isCustomerFacing,
} from 'store/services/device-status';
import { useAuth } from 'hooks/useAuth';
import { toTitleCase } from 'helpers';
import { useEffect, useState } from 'react';
import {
    useLazyGetSupplierDevicesEsQuery,
    constructShortDeviceIdQueryString,
} from 'store/services';
import { LoadingSpinner } from 'components/data/LoadingSpinner';

export interface DeviceStatusSummaryProps {
    shipment: Shipment;
    /** A supplierId for the supplier that an admin user wants to view data for. */
    viewAsSupplier?: string;
}

const DeviceStatusSummaryCell = ({
    viewAsSupplier,
    shipment,
}: DeviceStatusSummaryProps) => {
    const { user } = useAuth();
    const [query, { data, isLoading, isFetching }] =
        useLazyGetSupplierDevicesEsQuery();
    const [statusSummary, setStatusSummary] = useState<DeviceStatusSummary[]>(
        []
    );

    useEffect(() => {
        if (shipment) {
            const shortIdQueryString = constructShortDeviceIdQueryString([
                shipment,
            ]);

            const supplierId = viewAsSupplier ?? user?.protected?.supplierId;
            // Don't query for devices for the admin account.
            if (!supplierId || supplierId === '0') return;

            query({
                userUid: user?.uid ?? '',
                supplierId,
                queryConfig: {
                    limit: 1000,
                    shortIdQueryString,
                    sortField: 'shortDeviceId',
                    isAdminUser:
                        user?.protected?.supplierId?.toString() === '0',
                },
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, viewAsSupplier, shipment]);

    useEffect(() => {
        if (!isFetching && !isLoading && data?.results) {
            if (data.results) {
                const statusSummary: { [status: string]: number } = {};

                // Tally up how many devices are in each status.
                data.results.forEach((device: Device) => {
                    const status = device.status.toLowerCase();
                    if (status in statusSummary) statusSummary[status] += 1;
                    else statusSummary[status] = 1;
                });

                // Translate statusSummary values into an array that can be orderBy'd.
                const summary: DeviceStatusSummary[] = [];
                for (const key in statusSummary) {
                    const entry = {
                        status: key,
                        count: statusSummary[key],
                        isCustomerFacing: isCustomerFacing(key),
                    };
                    summary.push(entry);
                }
                setStatusSummary(summary);
            }
        }
    }, [data, isLoading, isFetching]);

    return (
        <div
            className={styles.chipContainer}
            data-testid="DeviceStatusSummaryCell"
        >
            {isLoading || isFetching ? (
                <LoadingSpinner size={'30px'} />
            ) : (
                <>
                    {statusSummary.map(
                        (status: DeviceStatusSummary, i: number) => {
                            if (
                                user?.protected?.supplierId === '0' ||
                                status.isCustomerFacing
                            ) {
                                return (
                                    <div
                                        className={styles.deviceStatusChip}
                                        style={{
                                            backgroundColor:
                                                getDeviceStatusColor(
                                                    status.status
                                                ),
                                        }}
                                        key={i}
                                        data-testid="ChipCell"
                                    >
                                        {toTitleCase(status.status)}:{' '}
                                        {status.count}
                                    </div>
                                );
                            }
                        }
                    )}
                </>
            )}
        </div>
    );
};

export default DeviceStatusSummaryCell;
